/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Grid, List } from "@cd2/components"
import { useState, useEffect } from "react"
import { useGlobalState } from "./GlobalStateProvider"

import { brandColors, colors, isMobile } from "./EOSThemeProvider"

import { truncate } from "@cd2/tools/build/es6"
import { getBranch } from "../apiRequests/branch"
import { useFetchData } from "../apiRequests/utils"
import { icons } from "../constants/icons"
import { useApi } from "../hooks/useApi"
import { useAsyncInterval } from "../hooks/useInterval"
import { eRoutes } from "../routes"
import { SageLogo } from "../stories/quoteOrders/SageLogo"
import { useSettings } from "../utils/useSettings"
import { useBetaFeatureAccess } from "./BetaFeatureAccess"
import { DropdownMenu } from "./DropdownMenu"
import { Lines } from "./Lines"
import { BadgeCount, Menu, MenuItem } from "./Menu"
import * as Sentry from "@sentry/react"
import { HEADER_HEIGHT } from "./SiteWrapper"
import { sidebarTooltipKey } from "../utils/linkTooltips"

export const SIDEBAR_WIDTH = 220

export function Sidebar({ className }: { className?: string; onLinkClick(): void }) {
  const { currentUser, signOut } = useGlobalState()
  const { isFortusSubBranch, isFortusBranch, defaultCountry } = useSettings()
  const { barScanOrders, orderRequestIndexView, disableBulkExporting } = useSettings()
  const betaFeature = useBetaFeatureAccess()

  const userCanExportToSage =
    currentUser.isAccounts || currentUser.isAdmin || currentUser.isSuperAdmin

  const api = useApi()

  const [counts, setCounts] = useState({
    quotesWithoutOrders: 0,
    openTickets: 0,
    pendingAccountCount: 0,
    tradeApplicationsCount: 0,
    rmas: 0,
    internalRmas: 0,
    hrItemsAwaitingApproval: 0,
    customerRequestInboxCount: 0,
    pendingOrCountIn: 0,
    pendingOrCountOut: 0,
    inboxInternalJobs: 0,
  })

  useEffect(() => {
    if (currentUser?.id) {
      Sentry.setUser({ email: currentUser.email, branch: currentUser.branch.name })
    }
  }, [currentUser?.id])

  async function fetchCounts() {
    if (!currentUser.branch.id) {
      return
    }
    try {
      const counts = await api.counts({ branchId: currentUser.branch.id })
      setCounts({
        quotesWithoutOrders: counts.acceptedQuotesWithoutOrders,
        openTickets: counts.openTickets,
        pendingAccountCount: counts.pendingAccounts,
        tradeApplicationsCount: counts.tradeApplications,
        rmas: counts.rmas,
        internalRmas: counts.internalRmas,
        hrItemsAwaitingApproval: counts.itemsAwaitingApproval,
        customerRequestInboxCount: counts.customerRequestInbox,
        pendingOrCountIn: counts.pendingOrCountIn,
        pendingOrCountOut: counts.pendingOrCountOut,
        inboxInternalJobs: counts.inboxInternalJobs,
      })
    } catch (error) {
      console.error("Error fetching counts, this is not important, will refetch in 3 mins", error)
    }
  }

  useAsyncInterval(fetchCounts, 1000 * 60 * 3)

  const showNewRma =
    !currentUser.branch.name.toLowerCase().includes("ssp") && defaultCountry === "United Kingdom"

  const customerSupportBadgeCount =
    counts.openTickets +
    (showNewRma ? counts.rmas + counts.internalRmas : 0) +
    (currentUser.canViewFortusLiveAccounts
      ? counts.pendingAccountCount + counts.tradeApplicationsCount
      : 0)

  interface IMenuLink {
    title: React.ReactNode
    icon?: any
    route: string
    badgeCount?: number | null
    exact?: boolean
    links?: IMenuLink[]
    hidden?: boolean
    color?: string
  }

  const orderRequestSidebarLink: IMenuLink | null =
    orderRequestIndexView === "incoming" || orderRequestIndexView === "outgoing"
      ? {
          route: eRoutes["/order-requests"],
          icon: icons.orderRequests,
          title: "Order Requests",
          color: colors.orders,
          badgeCount: counts.pendingOrCountIn + counts.pendingOrCountOut,
          links: undefined,
          hidden: currentUser.hrAreaOnly,
        }
      : null

  if (orderRequestSidebarLink) {
    const subLinks = [
      {
        route: eRoutes["/order-requests/incoming"],
        title: "Incoming",
        color: colors.orders,
        badgeCount: counts.pendingOrCountIn,
      },
      {
        route: eRoutes["/order-requests/outgoing"],
        title: "Outgoing",
        color: colors.orders,
        badgeCount: counts.pendingOrCountOut,
      },
    ].filter(Boolean)
    if (subLinks.length > 0) {
      orderRequestSidebarLink.links = subLinks
      // set route to top link path
      orderRequestSidebarLink.route = subLinks[0].route
      // clear badge count on "category"
      orderRequestSidebarLink.badgeCount = undefined
    }
  }

  const linksIfViewOnlyCustomer: IMenuLink[] = [
    {
      route: eRoutes["/customers"],
      title: "Customers",
      icon: icons.customers,
      color: colors.customers,
    },
    {
      route: eRoutes["/reports"],
      icon: icons.crm,
      title: "Reports",
    },
  ]

  const links: IMenuLink[] = [
    isMobile && {
      route: "/",
      icon: icons.home,
      title: "Dashboard",
      color: colors.default,
      exact: true,
    },
    {
      route: eRoutes["/quotes"],
      icon: icons.quotes,
      title: "Quotes",
      color: colors.quotes,
      badgeCount: counts.quotesWithoutOrders,
      hidden: currentUser.hrAreaOnly,
      links: betaFeature("quoteRequests")
        ? [
            {
              route: eRoutes["/quote-requests"],
              title: "Quote Requests",
              // icon: icons.quotes,
              color: colors.quotes,
              hidden: currentUser.hrAreaOnly,
              badgeCount: counts.inboxInternalJobs,
            },
          ]
        : undefined,
    },
    {
      route: eRoutes["/orders"],
      icon: icons.orders,
      title: "Orders",
      color: colors.orders,
      links: [
        barScanOrders &&
          userCanExportToSage &&
          !disableBulkExporting && {
            route: eRoutes["/orders/mass-export"],
            title: "Bulk Export",
            color: colors.orders,
          },
      ].filter(Boolean),
      hidden: currentUser.hrAreaOnly,
    },
    (isFortusSubBranch ||
      isFortusBranch ||
      currentUser.branch.id === "71f9f469-e5bb-4428-94ba-6dacc3653f6e") && {
      route: eRoutes["/goods-receipts"],
      icon: icons.orders,
      title: "Goods Receipts",
      color: colors.orders,
      hidden: currentUser.hrAreaOnly,
    },
    orderRequestSidebarLink,
    betaFeature("canViewNewWarehouseFeatures") && {
      route: "#",
      icon: icons.pendingPicks,
      title: "Warehouse",
      links: [
        {
          route: eRoutes["/warehouse/pending-picks"],
          title: "Pending Picks",
        },
      ].filter(Boolean),
    },
    {
      route: eRoutes["/deliveries"],
      icon: icons.deliveries,
      title: "Deliveries",
      color: colors.deliveries,
      links: [
        !barScanOrders &&
          userCanExportToSage &&
          !disableBulkExporting && {
            route: eRoutes["/orders/mass-export"],
            title: "Bulk Export",
            color: colors.orders,
          },
      ].filter(Boolean),
      hidden: currentUser.hrAreaOnly,
    },
    !barScanOrders && {
      route: eRoutes["/invoices"],
      title: "Invoices",
      icon: icons.invoices,
      color: colors.invoices,
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/jobs"],
      title: "Customer Requests",
      icon: icons.jobs,
      color: colors.jobs,
      badgeCount: counts.customerRequestInboxCount,
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/drivers"],
      title: "Drivers",
      icon: icons.drivers,
      color: colors.drivers,
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/customer-support"],
      title: "Customer Support",
      icon: icons.crm,
      color: colors.orders,
      badgeCount: customerSupportBadgeCount,
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/customers"],
      title: "Customers",
      icon: icons.customers,
      color: colors.customers,
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/crm"],
      title: "CRM",
      icon: icons.customers,
      color: colors.customers,
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/purchase-orders"],
      icon: icons.purchaseOrders,
      title: "Purchase Orders",
      color: colors.purchaseOrders,
      badgeCount: 0,
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/stock"],
      icon: icons.stock,
      title: "Stock Control",
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/reports"],
      icon: icons.crm,
      title: "Reports",
      hidden: currentUser.hrAreaOnly,
    },
    currentUser.canRunSageImports && {
      route: eRoutes["/sage-sync"],
      icon: icons.refresh,
      title: (
        <List
          css={css`
            align-items: center;
          `}
          size={6}
          horizontal
        >
          <span
            css={css`
              display: inline-block;
              width: 36px;
              position: relative;
              top: 3px;
            `}
          >
            <SageLogo color="white" />
          </span>
          <span>Sync</span>
        </List>
      ),
      hidden: currentUser.hrAreaOnly,
    },
    {
      route: eRoutes["/hr"],
      icon: icons.hr,
      title: "HR",
      badgeCount: counts.hrItemsAwaitingApproval,
      color: "",
    },
    (currentUser.canAccessSystemSettings ||
      currentUser.can_manage_customer_slide_show ||
      currentUser.canEditLinkTooltips ||
      currentUser.canManageRoles ||
      currentUser.isSuperAdmin) && {
      route: eRoutes["/system"],
      icon: icons.settings,
      title: "System",
      hidden: currentUser.hrAreaOnly,
    },
  ].filter(Boolean)

  return (
    <div
      css={css`
        position: fixed;
        width: ${SIDEBAR_WIDTH}px;
        overflow: auto;
        height: calc(100vh - ${HEADER_HEIGHT}px);
        left: 0;
        top: ${HEADER_HEIGHT}px;
        z-index: 12;
      `}
      className={className}
    >
      <div>
        <div
          css={css`
            min-height: calc(100vh - ${HEADER_HEIGHT}px);
            background-color: ${brandColors.darkNight};
            display: flex;
            flex-direction: column;
            padding-top: 32px;
          `}
        >
          <div
            css={css`
              height: 100%;
              width: 100%;
              position: absolute;
              pointer-events: none;
              top: 0;
              overflow: hidden;
            `}
          >
            <Lines />
          </div>
          <div
            css={css`
              flex: 1;
            `}
          >
            <div>
              <Menu
                items={(currentUser.customer_view_only ? linksIfViewOnlyCustomer : links).map(
                  link => ({
                    children: link.title,
                    icon: link.icon,
                    to: link.route,
                    tooltipKey: sidebarTooltipKey(link),
                    badgeCount: link.badgeCount ? link.badgeCount : undefined,
                    subMenuItems: link.links?.map(subLink => ({
                      children: subLink.title,
                      icon: subLink.icon,
                      to: subLink.route,
                      tooltipKey: sidebarTooltipKey(subLink),
                      badgeCount: subLink.badgeCount ? subLink.badgeCount : undefined,
                    })),
                    exact: link.exact,
                  }),
                )}
                light
              />
            </div>
          </div>
          <div
            css={css`
              padding: 16px 0 48px;
            `}
          >
            <Grid gap={8}>
              <div>
                <div
                  css={css`
                    color: white;
                    padding-left: 44px;
                    font-size: 13px;
                    padding-bottom: 6px;
                  `}
                >
                  {currentUser.name}
                </div>
                <BranchMenu />
                <MenuItem icon={icons.signOut} onClick={signOut} light>
                  Log out
                </MenuItem>
              </div>
            </Grid>
          </div>
        </div>
      </div>
    </div>
  )
}

function BranchMenu() {
  const { currentUser } = useGlobalState()
  const { data: branch, loading } = useFetchData(
    () => getBranch(currentUser.branch.id!),
    currentUser.branch.id,
  )

  if (!branch || loading) {
    return null
  }

  const currentBranchLinks = [
    { children: branch.name, displayIconSpacing: false, isHeading: true },
    {
      children: "My Branch",
      to: eRoutes["/manage-branch"],
      icon: icons.branches,
      exact: true,
    },
    {
      children: "Settings",
      to: eRoutes["/manage-branch/edit"],
      icon: icons.settings,
    },
    {
      children: "Branch Users",
      to: eRoutes["/manage-branch/internal-users"],
      icon: icons.users,
    },
  ].filter(Boolean)

  if (currentUser.isAdmin || currentUser.isSuperAdmin) {
    return (
      <DropdownMenu items={currentBranchLinks}>
        <MenuItem icon={icons.branches} light>
          {truncate(branch.name, { length: 20 })}
        </MenuItem>
      </DropdownMenu>
    )
  } else {
    return (
      <MenuItem
        icon={icons.branches}
        css={css`
          pointer-events: none;
        `}
        light
      >
        {truncate(branch.name, { length: 20 })}
      </MenuItem>
    )
  }
}
